import {
  add,
  concat,
  flow,
  get,
  keyBy,
  map,
  mapValues,
  placeholder,
  reduce,
  set,
  sortBy,
  sumBy,
  toString,
  update
} from 'lodash/fp';

import decodePlayer from './decodePlayer';
import decodeTeam from './decodeTeam';
import decodeGame from './decodeGame';
import { getTeams } from './decodeChallenge';

const mapValuesWithKey = mapValues.convert({ cap: false });

const normalizePositions = data => {
  const positionRules = get('rule_set.selection_rules.position_rules', data);
  const teamPosition = get('rule_set.selection_rules.team_selections', data);

  if (teamPosition) {
    positionRules.push({ ...teamPosition, key: teamPosition.short_label.toLowerCase() });
  }
  return flow(
    map(item => ({
      abbr: item.short_label,
      id: item.key,
      name: item.label
    })),
    keyBy('id')
  )(positionRules);
};

const decodeSlate = group => {
  const isRacing = group.is_leg;
  const games = keyBy(
    'id',
    group.games.map((g, i) => ({
      ...g,
      gameOrder: i + 1,
      label: isRacing ? `L${i + 1}` : undefined
    }))
  );

  const selectables = reduce(
    (memo, lineup) =>
      reduce(
        (memo2, selectable) => {
          const positionKey = (
            get('position.key', selectable) ||
            get('position.short_label', selectable) ||
            ''
          ).toLowerCase();
          const selectableId = toString(selectable.id);
          const lineupId = toString(lineup.id);
          const entryCount = lineup.entry_count;

          if (memo2[positionKey] && memo2[positionKey][selectableId]) {
            return flow(
              update([positionKey, selectableId, 'entryCount'], add(placeholder, entryCount)),
              update([positionKey, selectableId, 'lineups'], concat(placeholder, lineupId))
            )(memo2);
          }

          const game = get(selectable.game_id, games);

          const decodedSelectable = selectable.isTeam
            ? decodeTeam(selectable, {}, game)
            : decodePlayer(selectable, game);

          const selectableObject = {
            ...decodedSelectable,
            entryCount,
            lineups: [lineupId],
            image: get('avatar.small', selectable),
            positionAbbr: get('position.short_label', selectable),
            positionId: positionKey
          };

          if (memo2[positionKey]) {
            return set([positionKey, selectableId], selectableObject, memo2);
          }

          return set(
            positionKey,
            {
              [selectableId]: selectableObject
            },
            memo2
          );
        },
        memo,
        [...lineup.selection.players, ...lineup.selection.teams.map(t => ({ ...t, isTeam: true }))]
      ),
    {},
    group.current_user_lineups
  );

  return {
    id: group.id,
    isRacing,
    name: group.name,
    sportId: get('sports[0].id', group),
    games: group.games ? sortBy('start_time', Object.values(games)).map(decodeGame) : null,
    teams: group.games ? getTeams(group.games) : null,
    totalEntries: sumBy('entry_count', group.current_user_lineups),
    positions: mapValuesWithKey(
      (v, k) => ({
        ...v,
        players: selectables[k] || {}
      }),
      normalizePositions(group)
    )
  };
};

export default decodeSlate;
